import type {
  CashSummaryDto,
  InvoiceAgingDto,
  ProfitabilityRowDto,
  ReportsResponse,
} from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { lazy, Suspense } from 'react';

import { SkeletonRows } from '../_ops/SkeletonRows.js';
import { CostBreakdown } from './components/CostBreakdown.js';
import { JobBreakdownTable } from './components/JobBreakdownTable.js';
import { OutstandingInvoices } from './components/OutstandingInvoices.js';
import { PnlStatGrid } from './components/PnlStatGrid.js';
import { ProfitLeaderboards } from './components/ProfitLeaderboards.js';
import { RangeFilter } from './components/RangeFilter.js';
import { ReportsEmptyState } from './components/ReportsEmptyState.js';
import { ReportsHeader } from './components/ReportsHeader.js';

/**
 * The two chart cards load on demand.
 *
 * They pull in the chart system, and recharts plus motion weigh about 538 kB
 * raw (159 kB gzipped). Charts appear on this page and nowhere else, so bundling
 * them with the app would make sign-in, the dispatch board and every timesheet
 * pay for a page most sessions never open. Splitting here rather than inside the
 * chart keeps each chart's configuration children in one chunk with their parent,
 * which matters: the chart identifies those children by reference, and a lazy
 * boundary around one of them would break that check.
 */
/** Holds a chart card's height while its chunk downloads, so the page does not jump. */
function ChartCardFallback() {
  return <div aria-hidden="true" className="h-80 rounded-md border border-border bg-card" />;
}

const TrendChart = lazy(async () => ({
  default: (await import('./components/TrendChart.js')).TrendChart,
}));
const ExpenseCategoryChart = lazy(async () => ({
  default: (await import('./components/ExpenseCategoryChart.js')).ExpenseCategoryChart,
}));

import type { ReportsSearch } from './searchParams.js';
import { isHidden, type ReportFormatters, useReportFormatters } from './useReportFormatters.js';

/**
 * The reports screen on the locked Velocity brand, uplifted to the P22 redesign
 * grammar (D-04 re-skin). Composes the section components and renders the full
 * `/api/v1/reports` payload: the cash/aging/profitability sections PLUS the cash-
 * flow trend chart, the expense-by-category chart, the top/worst profit + margin
 * leaderboards, and an expandable per-job cost drilldown inside the job breakdown
 * table (Plan 22-28 — these were previously deferred). Each added section renders
 * only when its data array is non-empty (mirroring the existing `hasData` guard).
 * No hook/endpoint/calc change — this is a pure presentation pass over already-
 * returned data.
 *
 * Redesign grammar added here (UI-SPEC §4/§5):
 * - **Skeleton loading**: an optional `loading` flag renders `_ops/SkeletonRows`
 *   shimmer placeholders in place of the stat strip + job table on a refetch, so
 *   the layout doesn't jump (the Route's first-load `Spinner` is unchanged; this
 *   defaults off, preserving the existing render exactly).
 * - **Responsive table→card**: the job breakdown shows the semantic `<table>` at
 *   `md+` and a stacked label:value card list below `md` (no horizontal scroll on
 *   phones). Every color stays a locked token (BRAND-06).
 *
 * The header + range filter always show (so the user can re-query an empty
 * period); the data sections only appear when there is something to show
 * (mirrors the legacy `hasData` guard).
 */

const EMPTY_CASH: CashSummaryDto = {
  recordedIncome: 0,
  invoicedAmount: 0,
  collectedPayments: 0,
  recordedExpenses: 0,
  materialsCost: 0,
  subcontractorCost: 0,
  laborCost: 0,
  monthlyBillsCost: 0,
  fleetCost: 0,
  cashOutflow: 0,
  netCash: 0,
  openReceivables: 0,
};

const EMPTY_AGING: InvoiceAgingDto = {
  current: 0,
  days1to30: 0,
  days31to60: 0,
  days61to90: 0,
  days90Plus: 0,
  totalOpen: 0,
  overdueTotal: 0,
  openCount: 0,
};

export interface ReportsViewProps {
  report: ReportsResponse;
  search: ReportsSearch;
  /**
   * When true the data sections render skeleton shimmer rows instead of values
   * (a background refetch is in flight). Optional + defaults to `false`, so the
   * existing Route — which never sets it — renders exactly as before.
   */
  loading?: boolean;
}

export function ReportsView({ report, search, loading = false }: ReportsViewProps) {
  const { t } = useTranslation();
  const fmt = useReportFormatters();

  const cash = report.cash ?? EMPTY_CASH;
  const aging = report.aging ?? EMPTY_AGING;
  const rows = report.rows ?? [];
  const trend = report.trend ?? [];
  const expenseCategories = report.expenseCategories ?? [];
  const topProfitJobs = report.topProfitJobs ?? [];
  const worstProfitJobs = report.worstProfitJobs ?? [];
  const topMarginJobs = report.topMarginJobs ?? [];
  const worstMarginJobs = report.worstMarginJobs ?? [];
  const hasLeaderboards =
    topProfitJobs.length > 0 ||
    worstProfitJobs.length > 0 ||
    topMarginJobs.length > 0 ||
    worstMarginJobs.length > 0;

  const revenue = Number(cash.recordedIncome || 0);
  const collected = Number(cash.collectedPayments || 0);
  const costs = Number(cash.cashOutflow || 0);
  const hasData = rows.length > 0 || revenue > 0 || collected > 0 || costs > 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <ReportsHeader search={search} filterLabel={report.filter?.label} />
      <RangeFilter search={search} />

      {loading ? (
        <div className="grid gap-4">
          <section
            aria-label={t('reports:title')}
            className="rounded-md border border-border bg-card p-6"
          >
            <SkeletonRows rows={4} columns={2} label={t('common:status.loading')} />
          </section>
          <section className="rounded-md border border-border bg-card p-6">
            <SkeletonRows rows={6} columns={4} label={t('common:status.loading')} />
          </section>
        </div>
      ) : hasData ? (
        <>
          <PnlStatGrid cash={cash} fmt={fmt} />
          <CostBreakdown cash={cash} fmt={fmt} />
          <OutstandingInvoices cash={cash} aging={aging} fmt={fmt} />
          {trend.length > 0 ? (
            <Suspense fallback={<ChartCardFallback />}>
              <TrendChart points={trend} fmt={fmt} />
            </Suspense>
          ) : null}
          {expenseCategories.length > 0 ? (
            <Suspense fallback={<ChartCardFallback />}>
              <ExpenseCategoryChart categories={expenseCategories} fmt={fmt} />
            </Suspense>
          ) : null}
          {hasLeaderboards ? (
            <ProfitLeaderboards
              topProfit={topProfitJobs}
              worstProfit={worstProfitJobs}
              topMargin={topMarginJobs}
              worstMargin={worstMarginJobs}
              fmt={fmt}
            />
          ) : null}
          {/* Responsive job breakdown: semantic table at md+, stacked cards below md. */}
          <div className="hidden md:block">
            <JobBreakdownTable rows={rows} fmt={fmt} />
          </div>
          <div className="md:hidden">
            <JobBreakdownCards rows={rows} fmt={fmt} />
          </div>
        </>
      ) : (
        <ReportsEmptyState />
      )}
    </div>
  );
}

/**
 * The job-breakdown rows as stacked label:value cards (UI-SPEC §5 — tables reflow
 * to record cards below `md` so there is no horizontal scroll on phones). Same
 * data + same locked tokens as `JobBreakdownTable`; profit keeps the sign-driven
 * green/red status token (meaning carried by the value's own sign too, A11Y-01).
 */
function JobBreakdownCards({ rows, fmt }: { rows: ProfitabilityRowDto[]; fmt: ReportFormatters }) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t('reports:table.caption')}
      className="rounded-md border border-border bg-card p-4"
    >
      <h2 className="mb-3 font-semibold text-foreground text-xl">{t('reports:table.heading')}</h2>
      {rows.length === 0 ? (
        <p className="px-1 py-6 text-center text-muted-foreground text-sm">
          {t('reports:table.noJobs')}
        </p>
      ) : (
        <ul className="grid gap-3">
          {rows.map((row) => (
            <li key={row.id} className="rounded-md border border-border bg-background p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium text-foreground text-sm">
                  {row.job_name || t('reports:table.unnamedJob', { id: row.id })}
                </div>
                <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground text-xs">
                  {row.archived ? t('reports:table.archived') : row.status || '—'}
                </span>
              </div>
              {row.client ? (
                <div className="mt-0.5 text-muted-foreground text-xs">{row.client}</div>
              ) : null}
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                <CardStat label={t('reports:table.revenue')} value={fmt.money(row.income)} />
                <CardStat label={t('reports:table.expenses')} value={fmt.money(row.expenses)} />
                <CardStat label={t('reports:table.labor')} value={fmt.money(row.labor)} />
                <CardStat label={t('reports:table.margin')} value={fmt.percent(row.margin)} />
                <CardStat
                  label={t('reports:table.profit')}
                  value={fmt.money(row.profit)}
                  valueClassName={
                    isHidden(row.profit as number | null)
                      ? undefined
                      : row.profit >= 0
                        ? 'text-success'
                        : 'text-destructive'
                  }
                />
                <CardStat
                  label={t('reports:table.owed')}
                  value={fmt.money(row.openAr)}
                  valueClassName={row.openAr > 0 ? 'text-warning' : undefined}
                />
              </dl>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CardStat({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </dt>
      <dd className={`font-mono font-medium text-foreground text-sm ${valueClassName ?? ''}`}>
        {value}
      </dd>
    </div>
  );
}
